/**
 * FmCorridorTrajectoryChart.axes — pure axis math for the corridor-trajectory
 * figure: data-derived x/y domains with explicit snapped ticks and the served
 * value collection. Split from FmCorridorTrajectoryChart.layout to keep both
 * modules under the modular-rewrite size gate; no JSX and no Recharts here.
 */
import type { FmStoryCorridorTrajectory } from './FmCorridorTrajectory.types';

/**
 * The y tick step adapts to the served range so a near-empty refusal strip is
 * not drowned in dead axis space (visual-QA F5): 5 m²/ha above a ~20-unit
 * range, 2 above ~8, else 1. The axis never invents finer resolution than
 * whole steps.
 */
export function yTickStep(range: number): number {
  if (range > 20) return 5;
  if (range > 8) return 2;
  return 1;
}

/**
 * The y domain starts on the snapped step just below min(data, statutory
 * floor) — the floor is the story's bottom, not zero — and ends on the step
 * above the data maximum. One extra step is padded ONLY when the data sits
 * exactly on the boundary step; a partial step already is the headroom.
 *
 * `padAboveMax` (the compact refusal strip, visual-QA round-2 B5): when no
 * curves set the extent the maximum IS the subject dot, and a dot within a
 * fraction of a step of the top edge reads as pinned to the frame — so the
 * strip guarantees at least one full step of air above the maximum.
 */
export function yDomainAndTicks(
  ys: readonly number[],
  padAboveMax = false,
): {
  domain: [number, number];
  ticks: number[];
} {
  const min = Math.min(...ys);
  const max = Math.max(...ys);
  const step = yTickStep(max - min);
  let lower = Math.floor(min / step) * step;
  if (lower === min) lower -= step;
  // Basal area cannot be negative: a served zero (a clearcut) sits on the
  // axis floor instead of a step of empty negative axis below it.
  if (min >= 0) lower = Math.max(0, lower);
  let upper = Math.ceil(max / step) * step;
  if (upper === max) upper += step;
  if (padAboveMax && upper - max < step) upper += step;
  const ticks: number[] = [];
  for (let tick = lower; tick <= upper; tick += step) ticks.push(tick);
  return { domain: [lower, upper], ticks };
}

/**
 * Explicit x ticks on a snapped step — no crowding terminal tick pairs — with
 * a GUARANTEED minimum of three ticks (visual-QA F4). A zero-range x set (the
 * all-refusals strip serves only the subject's x) takes a symmetric integer
 * window around the value; a fractional sub-step range that would otherwise
 * snap to no tick at all widens to the enclosing integer grid.
 */
export function xDomainAndTicks(
  xs: readonly number[],
  compact = false,
): {
  domain: [number, number];
  ticks: number[];
} {
  const min = Math.min(...xs);
  const max = Math.max(...xs);
  const range = max - min;
  if (range === 0) {
    const centre = Math.round(min);
    return {
      domain: [centre - 2, centre + 2],
      ticks: [centre - 2, centre - 1, centre, centre + 1, centre + 2],
    };
  }
  // A phone-width plot cannot seat ten tick labels (round-2 n6): compact
  // mode widens the snapped step until at most six ticks print.
  let step = range > 18 ? 5 : range > 9 ? 2 : 1;
  if (compact) {
    const steps = [1, 2, 5, 10, 20];
    while (Math.floor(range / step) + 1 > 6) {
      const next = steps[steps.indexOf(step) + 1];
      if (next === undefined) break;
      step = next;
    }
  }
  let ticks: number[] = [];
  for (let tick = Math.ceil(min / step) * step; tick <= max; tick += step) ticks.push(tick);
  if (ticks.length < 3) {
    // Sub-step served range: fall back to the enclosing integer grid, widened
    // symmetrically until at least three ticks exist.
    let lower = Math.floor(min);
    let upper = Math.ceil(max);
    while (upper - lower < 2) {
      lower -= 1;
      upper += 1;
    }
    ticks = [];
    for (let tick = lower; tick <= upper; tick += 1) ticks.push(tick);
    return { domain: [lower - 0.5, upper + 0.5], ticks };
  }
  return { domain: [min - 1, max + 1], ticks };
}

export function collectAxisValues(trajectory: FmStoryCorridorTrajectory): {
  xs: number[];
  ys: number[];
} {
  const xs: number[] = [trajectory.subject.x];
  const ys: number[] = [trajectory.subject.y];
  trajectory.rates.forEach((rate) => {
    [rate.residual, rate.trigger, rate.residualUpper, rate.triggerUpper].forEach((series) => {
      series?.forEach((point) => {
        xs.push(point.x);
        ys.push(point.y);
      });
    });
  });
  (trajectory.trajectories ?? []).forEach((path) => {
    path.points.forEach((point) => {
      xs.push(point.x);
      ys.push(point.y);
    });
  });
  const floorValue = trajectory.statutoryFloor.valueY;
  if (floorValue !== undefined) ys.push(floorValue);
  return { xs, ys };
}
